import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, User, MessageSquare, BookOpen, CreditCard, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

interface ActivityLog {
  id: number;
  event_type: string;
  description: string;
  user_id: string | null;
  metadata: unknown;
  created_at: string;
  user_name?: string;
}

const eventTypeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  word_added: { label: 'Ord tillagt', icon: <BookOpen className="h-4 w-4" />, color: 'bg-green-500' },
  clue_added: { label: 'Ledtråd tillagd', icon: <BookOpen className="h-4 w-4" />, color: 'bg-emerald-500' },
  word_suggestion_submitted: { label: 'Ordförslag', icon: <BookOpen className="h-4 w-4" />, color: 'bg-yellow-500' },
  word_suggestion_approved: { label: 'Förslag godkänt', icon: <BookOpen className="h-4 w-4" />, color: 'bg-green-600' },
  clue_suggestion_submitted: { label: 'Ledtrådsförslag', icon: <BookOpen className="h-4 w-4" />, color: 'bg-yellow-400' },
  clue_suggestion_approved: { label: 'Ledtråd godkänd', icon: <BookOpen className="h-4 w-4" />, color: 'bg-green-500' },
  forum_topic_created: { label: 'Nytt ämne', icon: <MessageSquare className="h-4 w-4" />, color: 'bg-blue-500' },
  forum_reply_created: { label: 'Forumsvar', icon: <MessageSquare className="h-4 w-4" />, color: 'bg-blue-400' },
  user_registered: { label: 'Ny användare', icon: <UserPlus className="h-4 w-4" />, color: 'bg-purple-500' },
  subscription_upgraded: { label: 'Uppgradering', icon: <CreditCard className="h-4 w-4" />, color: 'bg-amber-500' },
  avatar_submitted: { label: 'Avatar uppladdad', icon: <User className="h-4 w-4" />, color: 'bg-indigo-500' },
  avatar_approved: { label: 'Avatar godkänd', icon: <User className="h-4 w-4" />, color: 'bg-green-500' },
  avatar_rejected: { label: 'Avatar nekad', icon: <User className="h-4 w-4" />, color: 'bg-red-500' },
};

export function ActivityLogAdmin() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Fetch user display names
      const userIds = [...new Set((data || []).map(l => l.user_id).filter(Boolean))] as string[];
      let userMap: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', userIds);
        
        userMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p.display_name;
          return acc;
        }, {} as Record<string, string>);
      }
      
      const logsWithNames = (data || []).map(log => ({
        ...log,
        user_name: log.user_id ? userMap[log.user_id] : undefined,
      }));
      
      setLogs(logsWithNames);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Kunde inte hämta aktivitetslogg');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventConfig = (eventType: string) => {
    return eventTypeConfig[eventType] || { 
      label: eventType, 
      icon: <User className="h-4 w-4" />, 
      color: 'bg-gray-500' 
    };
  };

  const getLogDetails = (log: ActivityLog): string => {
    const metadata = log.metadata as Record<string, unknown> | null;
    if (!metadata) return log.description;
    
    // Extract relevant details from metadata based on event type
    switch (log.event_type) {
      case 'word_added':
        return metadata.word ? `Ord: ${metadata.word}` : log.description;
      case 'clue_added':
        return metadata.word && metadata.clue 
          ? `${metadata.word}: ${String(metadata.clue).substring(0, 50)}` 
          : log.description;
      case 'forum_topic_created':
        return metadata.title ? `Ämne: ${metadata.title}` : log.description;
      case 'forum_reply_created':
        return metadata.author_name ? `Av: ${metadata.author_name}` : log.description;
      case 'word_suggestion_submitted':
      case 'word_suggestion_approved':
        return metadata.word ? `Ord: ${metadata.word}` : log.description;
      case 'clue_suggestion_submitted':
      case 'clue_suggestion_approved':
        return metadata.word && metadata.clue 
          ? `${metadata.word}: ${String(metadata.clue).substring(0, 40)}` 
          : log.description;
      case 'user_registered':
        return metadata.display_name ? `Namn: ${metadata.display_name}` : log.description;
      case 'subscription_upgraded':
        return metadata.new_tier ? `Ny nivå: ${metadata.new_tier}` : log.description;
      case 'avatar_submitted':
      case 'avatar_approved':
      case 'avatar_rejected':
        return log.description;
      default:
        return log.description;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Aktivitetslogg</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchLogs}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Uppdatera
        </Button>
      </CardHeader>
      <CardContent>
        {loading && logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Laddar aktivitetslogg...
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Ingen aktivitet registrerad ännu.
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {logs.map((log) => {
              const config = getEventConfig(log.event_type);
              return (
                <div 
                  key={log.id} 
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className={`p-2 rounded-full ${config.color} text-white shrink-0`}>
                    {config.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {config.label}
                      </Badge>
                      {log.user_name && (
                        <span className="text-xs font-medium text-foreground">
                          {log.user_name}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatTime(log.created_at)}
                      </span>
                    </div>
                    <p className="text-sm mt-1 truncate">
                      {getLogDetails(log)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
